// V8 Shadow — panneau d'observation admin (lecture seule, replié par défaut). Zéro action possible.
import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CaretDown, CaretRight, Eye, ArrowClockwise } from "@phosphor-icons/react";

const BAND = { basse: "bg-slate-100 text-slate-600", moyenne: "bg-amber-100 text-amber-800", haute: "bg-emerald-100 text-emerald-800", critique: "bg-red-100 text-red-800" };
const CONF = { haute: "text-emerald-700", moyenne: "text-amber-700", faible: "text-red-700" };

function Badge({ v, map = BAND, testid }) {
  if (!v) return <span className="text-slate-400 text-xs" data-testid={testid}>—</span>;
  return <span className={`pill text-[11px] uppercase tracking-wider ${map[v] || "bg-slate-100 text-slate-600"}`} data-testid={testid}>{v.replace(/_/g, " ")}</span>;
}
function Row({ k, children, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1 text-[13px]" data-testid={testid}>
      <span className="text-slate-500 shrink-0">{k}</span>
      <span className="text-slate-900 font-semibold text-right break-words">{children ?? "—"}</span>
    </div>
  );
}
function Codes({ items, max = 4, empty = "Aucun" }) {
  if (!items?.length) return <span className="text-slate-400 font-normal">{empty}</span>;
  return <span className="font-mono text-[12px]">{items.slice(0, max).map((x) => (typeof x === "string" ? x : x.code)).join(" · ")}{items.length > max ? ` +${items.length - max}` : ""}</span>;
}
function Section({ title, children, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={testid}>
      <div className="overline mb-1" style={{ color: "var(--cf-text-2)" }}>{title}</div>
      {children}
    </div>
  );
}
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

export default function ScoringV8ShadowPanel({ dossierId }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  if (!user?.is_admin) return null;

  const load = async () => {
    setLoading(true); setErr(null);
    try { const r = await api.get(`/dossiers/${dossierId}/scoring-v2`); setData(r.data); }
    catch (e) { setErr(e?.response?.data?.detail || "Lecture impossible"); }
    finally { setLoading(false); }
  };
  const toggle = () => { const n = !open; setOpen(n); if (n && !data) load(); };

  const s = data?.scoring; const f = s?.faisabilite; const p = s?.priorite; const a = s?.action;
  return (
    <div className="card-base p-4 md:p-5 mb-4 border-dashed border-2 border-slate-300" data-testid="scoring-v8-shadow-panel">
      <button type="button" onClick={toggle} className="w-full flex items-center justify-between gap-3 text-left" data-testid="scoring-v8-toggle">
        <div className="flex items-center gap-2">
          {open ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
          <span className="overline" style={{ color: "var(--cf-neutral-text)" }}>Scoring V8 — SHADOW</span>
          <span className="pill bg-slate-900 text-white text-[10px] uppercase tracking-wider">Admin</span>
        </div>
        <span className="text-[11px] text-slate-500 flex items-center gap-1"><Eye size={12} /> lecture seule</span>
      </button>
      {open && (
        <div className="mt-3 space-y-3" data-testid="scoring-v8-content">
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-[12px] font-bold text-slate-700" data-testid="scoring-v8-disclaimer">
            V8 SHADOW — n'influence pas le pilotage actuel
          </div>
          {loading && <div className="text-sm text-slate-400 animate-pulse">Chargement…</div>}
          {err && <div className="text-sm text-red-600" data-testid="scoring-v8-error">{err}</div>}
          {data && !s && !loading && <div className="text-sm text-slate-500" data-testid="scoring-v8-empty">Aucun résultat Shadow (mode : {data.mode}). Le dossier n'a pas de simulation associée ou le mode est désactivé.</div>}
          {s && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Section title="Faisabilité" testid="scoring-v8-faisabilite">
                <Row k="Score"><span data-testid="v8-faisabilite-score">{f.score ?? "null"}</span></Row>
                <Row k="Niveau"><Badge v={f.level} testid="v8-faisabilite-level" /></Row>
                <Row k="Scope">{f.scope}</Row>
                <Row k="Status">{f.status?.replace("_", " ")}</Row>
                <Row k="Confiance"><span className={CONF[f.confidence?.level] || ""}>{f.confidence?.level || "—"}</span></Row>
                <Row k="Gates"><Codes items={f.gates} empty="Aucun gate" /></Row>
                <Row k="Vigilance"><Codes items={f.points_vigilance} empty="Aucun" /></Row>
                {f.confidence?.missing_data?.length > 0 && <Row k="Manquant"><Codes items={f.confidence.missing_data} /></Row>}
              </Section>
              <Section title="Priorité" testid="scoring-v8-priorite">
                <Row k="Score"><span data-testid="v8-priorite-score">{p.score ?? "null"}</span></Row>
                <Row k="Bande"><Badge v={p.band} testid="v8-priorite-band" /></Row>
                {p.terminal_status && <Row k="Terminal">{p.terminal_status}</Row>}
                <Row k="Drivers"><Codes items={p.drivers} max={3} /></Row>
                <Row k="Overrides"><Codes items={p.overrides} empty="Aucun" /></Row>
                {p.next_recompute_at && <Row k="Prochain recalcul">{fmt(p.next_recompute_at)}</Row>}
              </Section>
              <Section title="Action recommandée" testid="scoring-v8-action">
                {(() => { const pa = a.primary || a; const nx = a.next || []; return (<>
                  <Row k="Maintenant"><span className="font-mono" data-testid="v8-action-code">{pa.code}</span> · {pa.label}</Row>
                  <Row k="Raisons"><Codes items={(pa.reasons || []).filter(Boolean)} empty="Aucune" /></Row>
                  {nx.length > 0 && (
                    <div className="mt-1 pt-1 border-t border-dashed border-slate-200" data-testid="v8-action-next">
                      {nx.map((n) => (
                        <div key={n.code}>
                          <Row k="Ensuite"><span className="font-mono">{n.code}</span> · {n.label}</Row>
                          <Row k="Pourquoi"><Codes items={(n.reasons || []).filter(Boolean)} empty="—" /></Row>
                        </div>
                      ))}
                    </div>
                  )}
                </>); })()}
              </Section>
              <Section title="Flags" testid="scoring-v8-flags">
                {s.flags.length === 0 ? <div className="text-sm text-slate-400">Aucun flag</div> : (
                  <ul className="space-y-0.5">
                    {s.flags.slice(0, 6).map((fl) => (
                      <li key={fl.code} className="text-[12px] flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${fl.severity === "bloquant" ? "bg-red-500" : fl.severity === "attention" ? "bg-amber-500" : "bg-slate-400"}`} />
                        <span className="font-mono text-slate-700">{fl.code}</span><span className="text-slate-500 truncate">{fl.label_courtier}</span>
                      </li>
                    ))}
                    {s.flags.length > 6 && <li className="text-[11px] text-slate-400">+{s.flags.length - 6} autres</li>}
                  </ul>
                )}
              </Section>
              <Section title="Technique" testid="scoring-v8-technique">
                <div className="grid grid-cols-2 gap-x-4 text-[12px] font-mono">
                  <Row k="scoring_version">{s.version}</Row>
                  <Row k="config_version">{s.config_version}</Row>
                  <Row k="engine_revision">{s.engine_revision}</Row>
                  <Row k="computed_from_revision">{String(s.computed_from_revision ?? "—")}</Row>
                  <Row k="computed_at">{fmt(s.computed_at)}</Row>
                  <Row k="source_revision">{String(data.scoring_source_revision ?? 0)}</Row>
                </div>
              </Section>
            </div>
          )}
          <button type="button" onClick={load} className="text-[12px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid="scoring-v8-refresh">
            <ArrowClockwise size={12} /> Relire
          </button>
        </div>
      )}
    </div>
  );
}
